import { digger } from '@cbre/digger';
import { addition, formatNumber, reduceAmountWith, areaUomAbbrev, defaultTxt } from '@miq/utils';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Amount } from 'uom';
import { Units } from 'uom-units';
import { ReportRecord } from '../../models/report.model';
import { DisplayFnArgumentsInfo, DisplayFunctionOptions, LovValues } from '../../models/template.model';
import { getRegularOrColloquialVal } from './template-lov-attributes.const';
import { isDash, mapAndFormatDynamicValue } from './utils/dynamic-data-retrievers';
import { PropertyType } from './config/us/us.common';

export const ZERO_SQM = Amount.create(0, Units.SquareMeter);
const SQUARE_FEET_ABBREVATIONS = ['Square Foot', 'Square Feet'];
const measurmentsNotConsider = [
  'Canopy Area',
  'Yard Area',
  'Loading Dock Area',
  'Site Area',
  'Land Area',
  'Hardstand Area',
];
const PARKING_COST_OPTIONS = {
  'NZD/PCPA': 1,
  'NZD/PCPM': 12,
  'NZD/PCPW': 52,
  '-': 1,
};

export function fitOutFieldValueFormatter(value: string[]) {
  return (
    value ||
    [].map(val => {
      if (!!val && !isDash(val) && val.toLowerCase() === 'no') {
        val = 'N';
      } else if (!!val && !isDash(val) && val.toLowerCase() !== 'unknown') {
        val = 'Y';
      }
      return val;
    })
  );
}
export function usageAmenitiesData(
  record: ReportRecord,
  arg: DisplayFnArgumentsInfo,
  usageType: string,
  maxCount: number,
  field: string
): string[] {
  const dataKey = usageType === PropertyType.Office ? 'data/property.property_usage' : 'data.property.property_usage';
  const usagesData = digger(record, dataKey);
  let specificdata = usagesData.ref_property_usage_type_desc === usageType ? usagesData : {};
  if (usageType === PropertyType.Office) {
    specificdata = usagesData.filter(usage => usage.ref_property_usage_type_desc === usageType);
  }
  arg = { ...arg, isRecordTableData: true, field };
  let amenitiesValues = mapAndFormatDynamicValue(specificdata, arg, null, null, false) as string[];
  amenitiesValues = usageType === PropertyType.Office ? flatten(amenitiesValues) : amenitiesValues;
  amenitiesValues = [...new Set(amenitiesValues)];
  if (Array.isArray(amenitiesValues) && amenitiesValues.length > maxCount) {
    amenitiesValues = amenitiesValues.slice(0, maxCount);
  }
  return isDash(amenitiesValues) || amenitiesValues == null || amenitiesValues.length === 0 ? ['-'] : amenitiesValues;
}

function flatten(values: any[]) {
  return Array.isArray(values) ? [].concat(...values.map(flatten)) : values;
}

export function annualRentAndOutgoingFormatter(
  askingRentval: string[],
  availabelSpaceval: string[],
  outGoingsSpaceVal: string[],
  outgoings: DisplayFnArgumentsInfo
): string[] {
  const result: string[] = (askingRentval || []).map((askingrent, index) => {
    let finalVal = 0;
    if (!isDash(askingrent[0]) && !isDash(availabelSpaceval[index][0]) && !isDash(outGoingsSpaceVal[index][0])) {
    }
    askingrent = !isDash(askingrent[0]) ? askingrent[0] : '0';
    const spaceVal = !isDash(availabelSpaceval[index][0]) ? availabelSpaceval[index][0] : '0';
    const outgoingVal = !isDash(outGoingsSpaceVal[index][0]) ? outGoingsSpaceVal[index][0] : '0';
    finalVal =
      (parseFloat(askingrent.replace(/,/g, '')) + parseFloat(outgoingVal.replace(/,/g, ''))) *
      parseFloat(spaceVal.replace(/,/g, ''));
    return `$${formatNumber(finalVal, undefined, outgoings.dataFormat)}`;
  });
  return result;
}

export function areaSqmDataConversion(
  fieldTypeValue: string[],
  areaSizeVal: number | number[],
  areasSizeUOm: string | string[],
  options: DisplayFunctionOptions,
  isTotalHasSpecificFormat: boolean,
  addSqm: boolean,
  record: ReportRecord,
  parkingCountArg?: DisplayFnArgumentsInfo
) {
  let finalAreaVals;
  const areaSizeObj = Array.isArray(areaSizeVal) ? areaSizeVal : [areaSizeVal];
  const areaSizeUomObj = Array.isArray(areasSizeUOm) ? areasSizeUOm : [areasSizeUOm];
  finalAreaVals = (areaSizeObj || []).map((val, index) => {
    const areasSizeUomVal = areaSizeUomObj[index];
    const areaAndUom = [{ val, areasSizeUomVal }];
    let result;
    if (SQUARE_FEET_ABBREVATIONS.includes(areaSizeUomObj[index])) {
      result = convertSFToSQM(val);
    } else {
      result = reduceAmountWith(areaAndUom || [], {
        dataset: { value: 'val', uom: 'areasSizeUomVal' },
        operation: addition,
        initialValue: ZERO_SQM,
      }).value;
    }
    return `${formatNumber(result, undefined, '.0-2')}`;
  });
  const uomSymbol = areaUomAbbrev('Square Meter');
  if (options.isPPTHasSpecificFormat) {
    let areaSumVal;
    let fieldAreaValues = [];
    if (finalAreaVals.length && fieldTypeValue.length) {
      fieldTypeValue.forEach((type, index) => {
        if (!measurmentsNotConsider.find(measurment => measurment === type)) {
          fieldAreaValues.push(finalAreaVals[index]);
        }
      });
    }
    const finalArea = isTotalHasSpecificFormat ? fieldAreaValues : finalAreaVals;
    areaSumVal = !!finalArea ? finalArea.reduce((acc, val) => acc + parseFloat(val.replace(/,/g, '')), 0) : 0;
    areaSumVal = `${formatNumber(areaSumVal, undefined, '.0-2')}`;
    areaSumVal = `${areaSumVal} ${uomSymbol}`;
    if (addSqm) {
      finalAreaVals = finalAreaVals.map(element => {
        return (element = `${element} ${uomSymbol}`);
      });
    }
    if (parkingCountArg) {
      const parkinArea = mapAndFormatDynamicValue(record, parkingCountArg);
      finalAreaVals = !!finalAreaVals
        ? [...finalAreaVals, parkinArea[0] != defaultTxt ? parkinArea[0] : '', areaSumVal]
        : ['-'];
    } else {
      finalAreaVals = !!finalAreaVals ? [...finalAreaVals, areaSumVal] : ['-'];
    }
    return finalAreaVals;
  }
  if (addSqm) {
    finalAreaVals = finalAreaVals.map(element => {
      return (element = `${element} ${uomSymbol}`);
    });
  }
  return finalAreaVals;
}

export function convertSFToSQM(val) {
  return val / 10.764;
}

export function getArrayLovAttributesFormattedData(
  attributeInfo: DisplayFnArgumentsInfo,
  values: string[],
  options: DisplayFunctionOptions
): Observable<string | string[]> {
  return attributeInfo.lovValues.pipe(
    map((lov: LovValues[]) => {
      let data = values.map(value => {
        const res: string | string[] = getRegularOrColloquialVal(value, lov);
        return Array.isArray(res) ? res.join() : res;
      });
      data = !!values.length && !data.length ? values : data;
      const result = options.isPPTHasSpecificFormat ? data : data.join(', ');
      return result;
    }),
    catchError(() => {
      const result = options.isPPTHasSpecificFormat ? values : values.join(', ');
      return of(result);
    })
  );
}

export function amenitiesData(
  values: string[],
  amenities: LovValues[],
  options: DisplayFunctionOptions
): string | string[] {
  let data = values.map(value => {
    const res: string | string[] = getRegularOrColloquialVal(value, amenities);
    return Array.isArray(res) ? res.join() : res;
  });
  data = !!values.length && !data.length ? values : data;
  const result = options.isPPTHasSpecificFormat ? data : data.join(', ');
  return result;
}

export function getPropertyMeasurementsFieldsData(
  record: ReportRecord,
  options: DisplayFunctionOptions,
  fieldVal: string,
  fieldTypeVal: string
) {
  const fieldType = options.isPPTHasSpecificFormat
    ? `data.property_measurement_availability/${fieldTypeVal}`
    : `${fieldTypeVal}`;
  const field = options.isPPTHasSpecificFormat ? `data.property_measurement_availability/${fieldVal}` : `${fieldVal}`;
  const uomField = options.isPPTHasSpecificFormat
    ? `data.property_measurement_availability/${fieldVal}_uom`
    : `${fieldVal}_uom`;
  record = options.isPPTHasSpecificFormat ? record : record.data[0];
  const fieldTypeValue = digger(record, fieldType);
  const measurementVal = digger(record, field);
  const uomVal = digger(record, uomField);
  return { fieldTypeValue, measurementVal, uomVal };
}

export function getRentFieldsData(
  record: ReportRecord,
  options: DisplayFunctionOptions,
  fieldMeasurement: string,
  fieldRent: string,
  parkingCostArg: DisplayFnArgumentsInfo,
  parkingCountArg: DisplayFnArgumentsInfo
) {
  const measurementVal = options.isPPTHasSpecificFormat
    ? `data.property_measurement_availability/${fieldMeasurement}`
    : `${fieldMeasurement}`;
  const measurementUomVal = options.isPPTHasSpecificFormat
    ? `data.property_measurement_availability/property_measurement_size_uom`
    : `property_measurement_size_uom`;
  const rentVal = options.isPPTHasSpecificFormat
    ? `data.property_measurement_availability/${fieldRent}`
    : `${fieldRent}`;
  record = options.isPPTHasSpecificFormat ? record : record.data[0];
  let measuredDiggerVal = digger(record, measurementVal);
  const measuredDiggerUomVal = digger(record, measurementUomVal);
  const rentDiggedVal = digger(record, rentVal);
  if (options.isPPTHasSpecificFormat) {
    const rentPPTVal = (rentDiggedVal || []).map((acc, val) => {
      let measuredVal = measuredDiggerVal ? measuredDiggerVal[val] : 0;
      if (measuredDiggerUomVal && measuredDiggerVal) {
        const measuredUOMVal = measuredDiggerUomVal[val];
        measuredVal = getConvertedData(measuredVal, measuredUOMVal);
      }
      return acc && measuredDiggerVal ? acc * measuredVal : defaultTxt;
    });
    let arrOfResult = rentPPTVal;
    let rentSumVal = rentPPTVal.reduce((acc, val) => {
      const value = typeof val === 'number' ? val : 0;
      return acc + value;
    }, 0);
    let outputVal;
    const parkingCost = mapAndFormatDynamicValue(record, { ...parkingCostArg, uomArgument: null, uomId: null });
    const parkingCount = mapAndFormatDynamicValue(record, parkingCountArg);
    const parkingCostUOM = mapAndFormatDynamicValue(record, { ...parkingCostArg.uomArgument });
    if (
      parkingCost &&
      parkingCost.length &&
      parkingCount &&
      parkingCount.length &&
      Number(parkingCost[0]) &&
      Number(parkingCount[0])
    ) {
      outputVal = Number(parkingCost[0]) * Number(parkingCount[0]);
      if (parkingCostUOM) {
        outputVal = outputVal * PARKING_COST_OPTIONS[(parkingCostUOM as string) || '-'];
      }
      arrOfResult = [...rentPPTVal, outputVal];
    } else {
      arrOfResult = [...rentPPTVal, '-'];
    }
    rentSumVal += outputVal;
    arrOfResult = [...arrOfResult, rentSumVal !== 0 ? rentSumVal : '-'];
    return arrOfResult.map(val => {
      if (val != defaultTxt) {
        val = `$${formatNumber(val, undefined, '.0-2')}`;
      }
      return val;
    });
  } else {
    if (measuredDiggerVal && measuredDiggerUomVal) {
      measuredDiggerVal = getConvertedData(measuredDiggerVal, measuredDiggerUomVal);
    }
    const rentPaVal = rentDiggedVal && measuredDiggerVal ? rentDiggedVal * measuredDiggerVal : defaultTxt;
    return rentPaVal != defaultTxt ? `$${formatNumber(rentPaVal, undefined, '.0-2')}` : rentPaVal;
  }
}

function getConvertedData(measuredVal: number, measuredUOMVal: string) {
  const areaAndUom = [{ measuredVal, measuredUOMVal }];
  if (SQUARE_FEET_ABBREVATIONS.includes(measuredUOMVal)) {
    return convertSFToSQM(measuredVal);
  } else {
    return reduceAmountWith(areaAndUom || [], {
      dataset: { value: 'measuredVal', uom: 'measuredUOMVal' },
      operation: addition,
      initialValue: ZERO_SQM,
    }).value;
  }
}

export function getAreaSqmValues(
  record: ReportRecord,
  options: DisplayFunctionOptions,
  isTotalHasSpecificFormat: boolean,
  parkingCountArg?: DisplayFnArgumentsInfo,
  addSqm = false,
  fieldVal = 'property_measurement_size',
  fieldType = 'property_element_type_desc'
): string[] {
  const { fieldTypeValue, measurementVal, uomVal } = getPropertyMeasurementsFieldsData(
    record,
    options,
    fieldVal,
    fieldType
  );
  const areaSqmVal = areaSqmDataConversion(
    fieldTypeValue,
    measurementVal,
    uomVal,
    options,
    isTotalHasSpecificFormat,
    addSqm,
    record,
    parkingCountArg
  );
  return areaSqmVal;
}

export function getPropertyUsagesData(record: ReportRecord, usageType: string) {
  const usagesData = digger(record, 'associatedProperty.usages');
  const specificUsageData = (usagesData || []).find(data => data.ref_property_usage_type_desc === usageType);
  return specificUsageData;
}

export function getMediaInformation(record: ReportRecord) {
  return !!record.associatedProperty &&
    !!record.associatedProperty.getMediaInformation &&
    typeof record.associatedProperty.getMediaInformation === 'function'
    ? record.associatedProperty.getMediaInformation()
    : null;
}
